import type { api } from '@udm/contracts';
import { MapPin, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ProtocolTag } from '../../ui/components';
import { formatNumber, formatRelative } from '../../ui/format';

/** Card for a discovered (not adopted) device picked on the map: identity, why it is not adopted, where it is. */
export function DiscoveredCard({ d, onClose }: { d: api.DiscoveredDevice; onClose: () => void }) {
  const { t } = useTranslation();
  const pos = d.adoption?.position;
  return (
    <aside className="device-card discovered-card" aria-label={d.name}>
      <header className="device-card-head">
        <span className="device-card-icon discovered-icon">
          <MapPin size={20} strokeWidth={1.6} />
        </span>
        <div className="device-card-title">
          <strong className="ellipsis toolbar">
            {d.identifiers[0]?.value ?? d.name} <ProtocolTag protocol={d.protocol} />
          </strong>
          <span className="muted small">{t('map.discovered.title')}</span>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
          <X size={16} />
        </button>
      </header>
      <dl className="kv small">
        <dt>{t('map.discovered.status')}</dt>
        <dd>
          {d.adoption
            ? t(`discovery.status.${d.adoption.status}` as 'discovery.status.waiting', {
                defaultValue: d.adoption.status,
              })
            : '—'}
        </dd>
        <dt>{t('map.discovered.online')}</dt>
        <dd>{d.online ? t('presence.online') : t('presence.offline')}</dd>
        {pos && (
          <>
            <dt>{t('map.discovered.position')}</dt>
            <dd className="mono">
              {formatNumber(pos.lat, 5)}, {formatNumber(pos.lon, 5)}
            </dd>
            <dt>{t('map.discovered.positionAt')}</dt>
            <dd>{formatRelative(d.adoption?.position_at ?? undefined)}</dd>
          </>
        )}
        {d.identifiers.slice(1).map((i) => [
          <dt key={`${i.scheme}-k`}>{i.scheme}</dt>,
          <dd key={`${i.scheme}-v`} className="mono">
            {i.value}
          </dd>,
        ])}
      </dl>
      <footer className="device-card-foot">
        <span className="muted small">{t('map.discovered.hint')}</span>
        <Link to={`/discovered?adopt=${d.id}`} className="btn btn-primary">
          {t('map.discovered.adopt')}
        </Link>
      </footer>
    </aside>
  );
}
